import useAudioRecorder from "@/hooks/useAudioRecorder";
import useRealTime from "@/hooks/useRealtime";

import { ChatStore } from "@/stores/ChatStore";
import { useEffect, memo, useCallback } from "react";
import Spinner from "@/components/ui/spinner";
import onReceivedInputAudioTranscriptionCompleted from "@/services/websocket-events/onReceivedInputAudioTranscriptionCompleted";
import onReceivedInputAudioBufferSpeechStarted from "@/services/websocket-events/onReceivedInputAudioBufferSpeechStarted";
import onReceivedResponseCreated from "@/services/websocket-events/onReceivedResponseCreated";
import onReceivedExtensionMiddleTierToolResponse from "@/lib/websocket-events/onReceivedExtensionMiddleTierToolResponse";
import MicHandlerButtons from "@/components/ui/custom/mic-handler-buttons/mic-handler-buttons";
import toaster from "@/lib/toaster";
import SettingsStore from "@/stores/SettingsStore";
import useAudioPlayer from "@/hooks/useAudioPlayer";
import { EMicStatus } from "@/types/TMicStatus";
import { fetchCustomerSupportResponse } from "@/services/schaden-ki/fetch-customer-support-response";
import { featureFlags } from "@/lib/featureFlags";
import { bbLog, bbWarn, previewText } from "@/lib/blockbrainTrace";

const BLOCKBRAIN_TOOL_HINT =
  "\n\nDu hast zusaetzlich Zugriff auf das Tool 'ask_blockbrain'. " +
  "REGELN: " +
  "(1) Sobald die Nutzerfrage das Wort 'Blockbrain' enthaelt " +
  "(z. B. 'Frage Blockbrain nach ...', 'Was sagt Blockbrain zu ...'), " +
  "rufe IMMER 'ask_blockbrain' mit der eigentlichen Frage auf. " +
  "(2) Wurde Blockbrain im aktuellen Gespraech bereits einmal befragt, dann nutze 'ask_blockbrain' AUCH FUER FOLGEFRAGEN, " +
  "die sich auf die Blockbrain-Antwort beziehen (z. B. Praezisierungen, Rueckfragen, weitere Details). " +
  "Erst wenn der Nutzer das Thema klar wechselt oder explizit zurueck zur Schadenmeldung moechte, hoere mit Blockbrain auf. " +
  "(3) Gib die vom Tool zurueckgegebene Antwort woertlich und ohne Markdown-Formatierung weiter. " +
  "Erfinde KEINE Inhalte, die nicht im Tool-Ergebnis stehen. " +
  "(4) Beginne die Antwort niemals mit Floskeln wie 'Das hat nicht geklappt', 'Leider', 'Tut mir leid', " +
  "'Blockbrain sagt dazu woertlich' oder aehnlichen Einleitungen. " +
  "Sprich den Tool-Text direkt aus, so als waere es deine eigene Antwort. " +
  "Wenn das Tool selbst eine Rueckfrage stellt, gib genau diese Rueckfrage wieder - das bedeutet NICHT, dass etwas schiefgelaufen ist.";

const SCHADENKI_BLOCKBRAIN_FALLBACK_PROMPT =
  "Du bist der Sprach-Avatar der Versicherungsplattform. " +
  "Du beantwortest standardmaessig nur Fragen zur Schadenmeldung." +
  BLOCKBRAIN_TOOL_HINT;

function RealtimeHandler() {
  const usePushToTalk = SettingsStore(state => state.usePushToTalk);
  const useKnowledgeBase = SettingsStore(state => state.useKnowledgeBase);
  const useSchadenKI = SettingsStore(state => state.useSchadenKI);
  const systemMessage = SettingsStore(state => state.systemMessage);

  const blockbrainEnabled = featureFlags.blockbrain;
  const effectiveSystemMessage = (() => {
    if (useKnowledgeBase && !useSchadenKI) {
      return blockbrainEnabled ? `${systemMessage || ""}${BLOCKBRAIN_TOOL_HINT}` : systemMessage;
    }
    if (useSchadenKI && blockbrainEnabled) {
      return SCHADENKI_BLOCKBRAIN_FALLBACK_PROMPT;
    }
    return "";
  })();
  const isAvatarReady = ChatStore(state => state.isAvatarReady);
  const isRunning = ChatStore(state => state.isRunning);
  const micStatus = ChatStore(state => state.micStatus);
  const setAvatarStatus = ChatStore(state => state.setAvatarStatus);
  const setIsRunning = ChatStore(state => state.setIsRunning);
  const setIsAvatarReady = ChatStore(state => state.setIsAvatarReady);
  const appendDeltaBuffer = ChatStore(state => state.appendDeltaBuffer);
  const updateLastMessageTranscript = ChatStore(state => state.updateLastMessageTranscript);

  const { startSession, endSession, addUserAudio, inputAudioBufferClear, sendUserText, triggerResponse, resetIdleTimer } = useRealTime({
    enableInputAudioTranscription: true,
    useKnowledgeBase: useKnowledgeBase && !useSchadenKI, // Disable knowledge base when SchadenKI is active
    forceToolsAvailable: blockbrainEnabled, // Blockbrain tool stays callable even in SchadenKI mode
    systemMessage: effectiveSystemMessage,
    useSchadenKI, // Pass SchadenKI flag to disable auto-responses
    onWebSocketOpen: () => toaster("WebSocket-Verbindung geöffnet"),
    onWebSocketClose: () => toaster("WebSocket-Verbindung geschlossen", "error"),
    onWebSocketError: event => console.error("WebSocket error:", event),
    onIdleTimeout: () => ChatStore.getState().handleSessionTimeout(),
    onReceivedError: message => {
      console.error("error", message);
      if (ChatStore.getState().isWaitingForBlockbrain) {
        bbWarn("realtime.error.clearWaiting", { message });
        ChatStore.getState().setIsWaitingForBlockbrain(false);
        ChatStore.getState().setAvatarStatus("Bereit");
      }
    },
    onReceivedErrorEvent: errorEvent => {
      console.error("Realtime error event:", errorEvent);
      if (ChatStore.getState().isWaitingForBlockbrain) {
        bbWarn("realtime.errorEvent.clearWaiting", {
          type: errorEvent?.error?.type,
          code: errorEvent?.error?.code,
          message: errorEvent?.error?.message,
        });
        ChatStore.getState().setIsWaitingForBlockbrain(false);
        ChatStore.getState().setAvatarStatus("Bereit");
      }
    },
    onReceivedResponseDone: () => {
      // Final response finished without audio (e.g. tool error spoken as empty /
      // failed turn). Give audio deltas a short window, then clear the spinner.
      if (!ChatStore.getState().isWaitingForBlockbrain) return;
      bbLog("realtime.responseDone.waitingStillActive", {
        avatarStatus: ChatStore.getState().avatarStatus,
      });
      window.setTimeout(() => {
        if (ChatStore.getState().isWaitingForBlockbrain) {
          bbWarn("realtime.responseDone.clearWaitingNoAudio");
          ChatStore.getState().setIsWaitingForBlockbrain(false);
          if (ChatStore.getState().avatarStatus === "Frage Blockbrain") {
            ChatStore.getState().setAvatarStatus("Bereit");
          }
        }
      }, 5_000);
    },
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedInputAudioBufferSpeechStarted: message => onReceivedInputAudioBufferSpeechStarted(message),
    onReceivedResponseCreated: message => onReceivedResponseCreated(message),
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedResponseAudioTranscriptDelta: message => {
      // In SchadenKI mode the regular Realtime audio is suppressed because answers
      // come from fetchCustomerSupportResponse. With Blockbrain enabled we DO want
      // the audio through, but only for tool-triggered responses. Since SchadenKI
      // mode also sets create_response=false in the VAD config, the Realtime API
      // never auto-replies to free-form user speech - audio only arrives after a
      // tool call (rtmt.py issues an explicit response.create), so it is safe to
      // forward it here.
      const allowAudio = !useSchadenKI || blockbrainEnabled;
      if (message.delta && allowAudio) {
        // First real audio token of the spoken answer arrived - clear the
        // Blockbrain "thinking" spinner now (NOT in onReceivedResponseCreated,
        // because that also fires for the synthetic function_call response).
        if (ChatStore.getState().isWaitingForBlockbrain) {
          bbLog("realtime.audioDelta.clearWaiting", {
            deltaPreview: message.delta.slice(0, 40),
          });
          ChatStore.getState().setIsWaitingForBlockbrain(false);
          ChatStore.getState().setAvatarStatus("Sprechen");
        }
        appendDeltaBuffer(message.delta);
        updateLastMessageTranscript(message.delta, "assistant");
      }
    },
  });

  const { reset: resetAudioPlayer, stop: stopAudioPlayer } = useAudioPlayer();
  const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({
    onAudioRecorded: useCallback((audioData: string) => {
      // if (!textInputState.isOpen) {
      addUserAudio(audioData);
      // }
    }, [addUserAudio]),
  });

  // expose text sender through store for MessageInput
  // Wrap sendUserText to route to Schaden KI when enabled
  const sendTextWrapper = useCallback((text: string) => {
    // User activity (incl. SchadenKI form turns) must keep the idle timeout alive.
    resetIdleTimer();

    const hasTrigger = blockbrainEnabled && /\bblock\s*brain\b/i.test(text);
    const wantsExit = blockbrainEnabled && /\b(zur(ü|ue)ck zur schadenmeldung|stop blockbrain|exit blockbrain|blockbrain beenden|raus aus blockbrain)\b/i.test(text);

    let inBlockbrain = ChatStore.getState().inBlockbrainConversation;
    if (wantsExit && inBlockbrain) {
      bbLog("text.exitSticky");
      ChatStore.getState().setInBlockbrainConversation(false);
      inBlockbrain = false;
    } else if (hasTrigger && !inBlockbrain) {
      bbLog("text.enterSticky");
      ChatStore.getState().setInBlockbrainConversation(true);
      inBlockbrain = true;
    }

    const routeToRealtime = blockbrainEnabled && (hasTrigger || inBlockbrain);

    bbLog("text.send", {
      useSchadenKI,
      blockbrainEnabled,
      hasTrigger,
      sticky: inBlockbrain,
      routeToRealtime,
      preview: previewText(text),
      textLength: text.length,
    });

    if (useSchadenKI && !routeToRealtime) {
      bbLog("text.routeToSchadenKI");
      // Route to Schaden KI API
      setAvatarStatus("Denken");
      fetchCustomerSupportResponse(text);
    } else {
      bbLog("text.routeToRealtime", { blockbrain: routeToRealtime });
      // Route to Realtime API (also used for Blockbrain queries while SchadenKI is on)
      if (routeToRealtime) {
        setAvatarStatus("Frage Blockbrain");
        ChatStore.getState().setIsWaitingForBlockbrain(true);
        // Same safety net as the voice path (text previously had no timeout).
        window.setTimeout(() => {
          if (ChatStore.getState().isWaitingForBlockbrain) {
            bbWarn("text.timeout180s.clearWaiting");
            ChatStore.getState().setIsWaitingForBlockbrain(false);
            if (ChatStore.getState().avatarStatus === "Frage Blockbrain") {
              ChatStore.getState().setAvatarStatus("Bereit");
            }
          }
        }, 180_000);
      }
      sendUserText(text);
    }
  }, [useSchadenKI, blockbrainEnabled, sendUserText, setAvatarStatus, resetIdleTimer]);

  useEffect(() => {
    bbLog("session.config", {
      blockbrainEnabled,
      useSchadenKI,
      useKnowledgeBase,
      forceToolsAvailable: blockbrainEnabled,
      systemMessageLen: (effectiveSystemMessage || "").length,
      hasToolHint: (effectiveSystemMessage || "").includes("ask_blockbrain"),
    });
  }, [blockbrainEnabled, useSchadenKI, useKnowledgeBase, effectiveSystemMessage]);

  useEffect(() => {
    ChatStore.setState({ sendText: sendTextWrapper });
    return () => ChatStore.setState({ sendText: undefined });
  }, [sendTextWrapper]);

  useEffect(() => {
    ChatStore.setState({ triggerRealtimeResponse: triggerResponse });
    return () => ChatStore.setState({ triggerRealtimeResponse: undefined });
  }, [triggerResponse]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isAvatarReady) {
      startSession();
      setAvatarStatus("Bereit");
      resetAudioPlayer();
      toaster("Realtime session läuft");
      // onToggleListening(isRunning);
    }

    return () => {
      inputAudioBufferClear();
      stopAudioRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvatarReady]);

  // Handle push-to-talk state changes
  useEffect(() => {
    const shouldRecord = (usePushToTalk && micStatus === EMicStatus.recording) || !usePushToTalk;

    if (shouldRecord) {
      console.log("Starting audio recording - conditions met", { usePushToTalk, micStatus });
      startAudioRecording();
    } else {
      console.log("Stopping audio recording - conditions not met", { usePushToTalk, micStatus });
      stopAudioRecording();
    }
  }, [micStatus, usePushToTalk, startAudioRecording, stopAudioRecording]);

  useEffect(() => {
    if (!isRunning) {
      endSession();
      stopAudioRecording();
      resetAudioPlayer();
      stopAudioPlayer();
      inputAudioBufferClear();
      ChatStore.getState().setInBlockbrainConversation(false);
      ChatStore.getState().setIsWaitingForBlockbrain(false);
      console.log("🔴 Session ended");
    }
  }, [isRunning, setIsRunning, setAvatarStatus, setIsAvatarReady, endSession, resetAudioPlayer, inputAudioBufferClear, stopAudioRecording]);

  if (isRunning && !isAvatarReady) {
    return (
      <div className={"flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg"}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={"absolute bottom-0 left-[50%] z-10 mx-auto flex translate-x-[-50%] items-end justify-center pb-4 opacity-90"}>
      <MicHandlerButtons addUserAudio={addUserAudio} />
    </div>
  );
}

export default memo(RealtimeHandler);
